import { Button } from "@/components/ui/button";
import { Shield, LogOut, User } from "lucide-react";
import { useWeb3Modal } from '@web3modal/wagmi/react';
import { useAccount, useConfig, useConnect, useSignMessage } from 'wagmi';
import { useQuery } from "@tanstack/react-query";
import { verifyGitcoinPassport, getPassportScore } from "@/utils/gitcoinPassport";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useState } from "react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

export const WalletSection = () => {
  const { open } = useWeb3Modal();
  const { address, isConnected, connector } = useAccount();
  const config = useConfig();
  const { connect, connectors } = useConnect();
  const { signMessageAsync } = useSignMessage();
  const { toast } = useToast();
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [supabaseUser, setSupabaseUser] = useState<any>(null);
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  const clearWalletState = () => {
    config.setState((state) => ({
      ...state,
      connections: new Map(),
      current: null,
      status: 'disconnected',
    }));
  };

  const handleConnect = () => {
    // Authentication is only allowed after an explicit user action. This
    // prevents a persisted WalletConnect session from silently logging in.
    localStorage.removeItem('wallet_manually_disconnected');
    localStorage.setItem('wallet_connect_requested', 'true');
    const injectedConnector = connectors.find((item) => item.type === 'injected');
    if (injectedConnector) {
      connect({ connector: injectedConnector });
    } else {
      open();
    }
  };

  const { data: isVerified } = useQuery({
    queryKey: ['gitcoin-verification', address],
    queryFn: () => verifyGitcoinPassport(address || ''),
    enabled: !!address
  });

  const { data: score } = useQuery({
    queryKey: ['gitcoin-score', address],
    queryFn: () => getPassportScore(address || ''),
    enabled: !!address && isVerified === true
  });

  const handleVerifyWithGitcoin = () => {
    window.open('https://passport.gitcoin.co/', '_blank');
    toast({
      title: "Verification Started",
      description: "Please complete the verification process on Gitcoin Passport. Your status will update automatically once verified.",
    });
  };

  // Check Supabase auth status
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSupabaseUser(session?.user ?? null);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSupabaseUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  // If the user previously chose Disconnect, do not let a provider restore the
  // old WalletConnect/injected session on the next render.
  useEffect(() => {
    if (isConnected && localStorage.getItem('wallet_manually_disconnected') === 'true') {
      clearWalletState();
    }
  }, [isConnected, connector, config]);

  // Auto-authenticate when wallet connects (only once per wallet)
  useEffect(() => {
    const authenticateWithWallet = async () => {
      // Check localStorage flag to see if we've already attempted auth for this wallet
      const hasAttemptedAuth = localStorage.getItem(`wallet_auth_attempted_${address}`) === 'true';
      const hasManuallyDisconnected = localStorage.getItem('wallet_manually_disconnected') === 'true';
      
      const connectWasRequested = localStorage.getItem('wallet_connect_requested') === 'true';
      if (isConnected && address && connectWasRequested && !supabaseUser && !isAuthenticating && !hasAttemptedAuth && !hasManuallyDisconnected) {
        setIsAuthenticating(true);
        
        // Mark that we've attempted auth for this wallet
        localStorage.setItem(`wallet_auth_attempted_${address}`, 'true');
        
        try {
          const message = `Sign this message to authenticate with Matic Moon on Polygon.\n\nWallet: ${address}\nNetwork: Polygon`;
          
          const signature = await signMessageAsync({ 
            message,
            account: address as `0x${string}`,
          });
          
          // Sign in with wallet address as email (wallet-based auth)
          // Versioned identity avoids accounts created by the previous
          // timestamp-based password flow, which could never sign in again.
          const walletEmail = `${address.toLowerCase()}@wallet-v2.local`;
          
          // Try to sign in first
          let { error: signInError } = await supabase.auth.signInWithPassword({
            email: walletEmail,
            password: signature.slice(0, 72),
          });

          // If user doesn't exist, sign them up
          if (signInError?.message?.includes('Invalid login credentials')) {
            const { error: signUpError } = await supabase.auth.signUp({
              email: walletEmail,
              password: signature.slice(0, 72),
              options: {
                data: {
                  wallet_address: address.toLowerCase(),
                },
                emailRedirectTo: `${window.location.origin}/`,
              },
            });

            if (signUpError?.message?.includes('already registered')) {
              const retry = await supabase.auth.signInWithPassword({
                email: walletEmail,
                password: signature.slice(0, 72),
              });
              if (!retry.error) {
                setSupabaseUser((await supabase.auth.getUser()).data.user);
              } else {
                console.error('Wallet authentication failed:', retry.error);
              }
            } else if (signUpError) {
              console.error('Sign up error:', signUpError);
            } else {
              localStorage.removeItem('wallet_manually_disconnected');
              toast({
                title: "Authenticated!",
                description: "Your wallet is now connected to your profile.",
              });
            }
          } else if (!signInError) {
            localStorage.removeItem('wallet_manually_disconnected');
            toast({
              title: "Welcome Back!",
              description: "Your wallet is connected to your profile.",
            });
          }
        } catch (error: any) {
          // User rejected signature - this is normal, don't show error
          if (error?.message?.includes('User rejected')) {
            console.log('User canceled wallet signature');
          } else {
            console.error('Authentication error:', error);
          }
        } finally {
          setIsAuthenticating(false);
        }
      }
    };

    authenticateWithWallet();
  }, [isConnected, address, supabaseUser, isAuthenticating, signMessageAsync, toast]);

  const handleDisconnect = async () => {
    setIsDisconnecting(true);
    try {
      // Set flag to prevent auto-reconnect
      localStorage.setItem('wallet_manually_disconnected', 'true');
      
      // Clear the wallet-specific auth attempt flag
      if (address) {
        localStorage.removeItem(`wallet_auth_attempted_${address}`);
      }
      
      // Clear Wagmi state synchronously before WalletConnect can fail while
      // trying to communicate with its blocked secure frame.
      clearWalletState();
      await supabase.auth.signOut();
      const persistedKeys = Object.keys(localStorage).filter((key) => /^(wagmi|wc@|walletconnect)/i.test(key));
      persistedKeys.forEach((key) => localStorage.removeItem(key));
      localStorage.removeItem('wallet_connect_requested');
      
      // Clear local state
      setSupabaseUser(null);
      
      toast({
        title: "Disconnected",
        description: "Your wallet has been disconnected.",
      });
    } catch (error) {
      console.error('Disconnect error:', error);
      toast({
        title: "Error",
        description: "Failed to disconnect. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDisconnecting(false);
    }
  };

  const formatAddress = (addr: string) => {
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  };

  return (
    <div className="flex items-center space-x-4">
      {isConnected && !isVerified && (
        <Button 
          variant="outline" 
          size="sm"
          onClick={handleVerifyWithGitcoin}
          className="gap-2 hidden sm:flex"
        >
          <Shield className="h-4 w-4" />
          Verify with Gitcoin
        </Button>
      )}
      {isConnected && isVerified && (
        <div className="hidden sm:flex items-center gap-2 text-sm text-muted-foreground">
          <Shield className="h-4 w-4 text-green-500" />
          Score: {score || 0}
        </div>
      )}
      {isConnected && address && !isDisconnecting ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm">
              {formatAddress(address)}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="border border-border/40 backdrop-blur-xl shadow-xl"
            style={{
              backgroundColor: "hsl(var(--popover) / 0.96)",
              color: "hsl(var(--popover-foreground))",
            }}
          >
            <DropdownMenuItem disabled className="flex items-center gap-2">
              <User className="h-4 w-4" />
              {supabaseUser ? 'Authenticated' : 'Not authenticated'}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleDisconnect} className="flex items-center gap-2 text-destructive">
              <LogOut className="h-4 w-4" />
              Disconnect
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        <Button variant="ghost" size="sm" onClick={handleConnect} disabled={isDisconnecting}>
          {isDisconnecting ? 'Disconnecting...' : 'Connect'}
        </Button>
      )}
    </div>
  );
};
